import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { CreateFarmInput, DeviceInfo, FarmInfo, UpdateFarmInput, UserInfo } from '@grosync/common';
import {
  AdminTopBar,
  ViewScroll,
  Sym,
  IconButton,
  Avatar,
  StatusPill,
  StatusDot,
  RolePill,
  ModelTag,
  Button,
  type DeviceStatus,
} from '../../ui';
import { ContentToolbar } from '../../ui/ContentToolbar';
import { LoadingSpinner, PageError } from '../../components/LoadingSpinner';
import { fetchFarms } from '../../api/farms';
import { fetchDevices } from '../../api/devices';
import { fetchUsers } from '../../api/users';
import { useFarms } from './hooks/useFarms';
import { FarmFormModal, FARM_ICONS } from './components/FarmFormModal';
import { LocationPicker } from './components/LocationPicker';
import type { GeoLocation } from '@grosync/common';

/* ============================================================================
 * 농장 — 테이블 + 상세 드로어 (Claude Design 재구현)
 * ========================================================================= */

/** 백엔드 DeviceStatus → 디자인 ok/warn/off */
function toUiStatus(s: DeviceInfo['status']): DeviceStatus {
  switch (s) {
    case 'ACTIVE':
      return 'ok';
    case 'OFFLINE':
      return 'off';
    default:
      return 'warn'; // WARNING | ERROR
  }
}

type FilterKey = 'all' | 'ok' | 'warn' | 'off';

/** 농장 단위 파생 데이터 (장비 목록 + 상태 집계 + 농장주) */
interface FarmRow {
  farm: FarmInfo;
  owner: UserInfo | undefined;
  devices: Array<DeviceInfo & { ui: DeviceStatus }>;
  online: number;
  warns: number;
  status: DeviceStatus;
  region: string;
  crop: string;
}

/** 장비 상태 목록으로부터 농장 상태 산출 (최악값) */
function farmStatusOf(uiStatuses: DeviceStatus[]): DeviceStatus {
  if (!uiStatuses.length) return 'off';
  const hasWarn = uiStatuses.some((s) => s === 'warn');
  const hasOk = uiStatuses.some((s) => s === 'ok');
  const hasOff = uiStatuses.some((s) => s === 'off');
  if (hasWarn) return 'warn';
  if (hasOff && !hasOk) return 'off';
  return 'ok';
}

const FCOLS = '1.7fr 1.4fr 1.6fr 0.9fr 0.7fr 0.9fr 0.8fr';

export default function FarmsView(): React.ReactElement {
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState<FilterKey>('all');
  const [sel, setSel] = useState<FarmRow | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const { create, update } = useFarms();
  const [saveError, setSaveError] = useState<string | null>(null);

  const handleSave = (id: string, body: UpdateFarmInput): void => {
    setSaveError(null);
    update.mutate(
      { id, body },
      {
        onError: (err) => setSaveError((err as Error).message),
      },
    );
  };

  const handleCreate = (body: CreateFarmInput | UpdateFarmInput): void => {
    create.mutate(body as CreateFarmInput, {
      onSuccess: () => setCreateOpen(false),
      onError: (err) => alert(`농장 추가 실패: ${(err as Error).message}`),
    });
  };

  const farmsQuery = useQuery({ queryKey: ['farms'], queryFn: fetchFarms });
  const devicesQuery = useQuery({ queryKey: ['devices'], queryFn: fetchDevices });
  const usersQuery = useQuery({ queryKey: ['users'], queryFn: fetchUsers });

  const farms = farmsQuery.data ?? [];
  const devices = devicesQuery.data ?? [];
  const users = usersQuery.data ?? [];

  // ── 농장별 파생 행 ────────────────────────────────────────────────────────
  const farmRows = useMemo<FarmRow[]>(() => {
    const ownerById = new Map(users.map((u) => [u.id, u]));
    return farms.map((farm) => {
      const farmDevices = devices
        .filter((d) => d.farmId === farm.id)
        .map((d) => ({ ...d, ui: toUiStatus(d.status) }));
      const online = farmDevices.filter((d) => d.ui !== 'off').length;
      const warns = farmDevices.filter((d) => d.ui === 'warn').length;
      const status = farmStatusOf(farmDevices.map((d) => d.ui));
      return {
        farm,
        owner: ownerById.get(farm.ownerId),
        devices: farmDevices,
        online,
        warns,
        status,
        region: farm.location?.description ?? '지역 미지정',
        crop: farm.cropType ?? '작물 미지정',
      };
    });
  }, [farms, devices, users]);

  React.useEffect(() => {
    setSel((cur) => (cur ? farmRows.find((r) => r.farm.id === cur.farm.id) ?? cur : cur));
  }, [farmRows]);

  const counts = useMemo(
    () => ({
      all: farmRows.length,
      ok: farmRows.filter((r) => r.status === 'ok').length,
      warn: farmRows.filter((r) => r.status === 'warn').length,
      off: farmRows.filter((r) => r.status === 'off').length,
    }),
    [farmRows],
  );

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return farmRows.filter((r) => {
      const okF = filter === 'all' ? true : r.status === filter;
      const okQ =
        !needle ||
        [r.farm.name, r.region, r.crop, r.owner?.name].some((s) =>
          (s ?? '').toLowerCase().includes(needle),
        );
      return okF && okQ;
    });
  }, [farmRows, filter, q]);

  const filters: Array<{ key: FilterKey; label: string; n: number }> = [
    { key: 'all', label: '전체', n: counts.all },
    { key: 'ok', label: '정상', n: counts.ok },
    { key: 'warn', label: '경고', n: counts.warn },
    { key: 'off', label: '오프라인', n: counts.off },
  ];

  const isLoading = farmsQuery.isLoading || devicesQuery.isLoading || usersQuery.isLoading;
  const error = farmsQuery.error ?? devicesQuery.error ?? usersQuery.error;

  return (
    <>
      <AdminTopBar
        title="농장"
        subtitle={`전국 ${counts.all}곳 · 정상 ${counts.ok} · 경고 ${counts.warn} · 오프라인 ${counts.off}`}
      />

      <ViewScroll gap={18}>
        <ContentToolbar
          search={q}
          onSearch={setQ}
          searchPlaceholder="농장명 · 지역 · 농장주 검색"
          actions={
            <button
              type="button"
              onClick={() => setCreateOpen(true)}
              style={{
                height: 42,
                padding: '0 18px',
                borderRadius: 9999,
                border: 0,
                cursor: 'pointer',
                background: 'var(--md-sys-color-primary)',
                color: 'var(--md-sys-color-on-primary)',
                font: '600 14px/1 var(--gs-font-kr)',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <Sym name="add" size={18} />
              농장 추가
            </button>
          }
          filters={filters.map((ff) => {
            const active = filter === ff.key;
            return (
              <button
                key={ff.key}
                type="button"
                onClick={() => setFilter(ff.key)}
                style={{
                  height: 36,
                  padding: '0 14px',
                  borderRadius: 9999,
                  cursor: 'pointer',
                  border: active ? 0 : '1px solid var(--md-sys-color-outline-variant)',
                  background: active ? 'var(--md-sys-color-secondary-container)' : 'transparent',
                  color: active
                    ? 'var(--md-sys-color-on-secondary-container)'
                    : 'var(--md-sys-color-on-surface-variant)',
                  font: `${active ? 600 : 500} 13px/1 var(--gs-font-kr)`,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                {ff.label}
                <span className="gs-num" style={{ fontWeight: 700, opacity: active ? 1 : 0.7 }}>
                  {ff.n}
                </span>
              </button>
            );
          })}
        />
        {isLoading ? (
          <LoadingSpinner />
        ) : error ? (
          <PageError message={(error as Error).message} />
        ) : (
          <>
            {/* 테이블 */}
            <div
              style={{
                background: 'var(--md-sys-color-surface-container-lowest)',
                border: '1px solid var(--md-sys-color-outline-variant)',
                borderRadius: 18,
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: FCOLS,
                  gap: 14,
                  padding: '13px 22px',
                  borderBottom: '1px solid var(--md-sys-color-outline-variant)',
                  background: 'var(--md-sys-color-surface-container-low)',
                }}
              >
                {['농장', '농장주', '지역 · 작물', '상태', '장비', '온라인', '경고'].map((h) => (
                  <span
                    key={h}
                    style={{
                      font: '600 12px/16px var(--gs-font-kr)',
                      color: 'var(--md-sys-color-on-surface-variant)',
                    }}
                  >
                    {h}
                  </span>
                ))}
              </div>

              {rows.map((r, i) => (
                <div
                  key={r.farm.id}
                  className="gs-row"
                  onClick={() => setSel(r)}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: FCOLS,
                    gap: 14,
                    padding: '13px 22px',
                    alignItems: 'center',
                    cursor: 'pointer',
                    background:
                      sel?.farm.id === r.farm.id ? 'var(--md-sys-color-secondary-container)' : 'transparent',
                    borderBottom:
                      i === rows.length - 1 ? 0 : '1px solid var(--md-sys-color-outline-variant)',
                  }}
                >
                  {/* 농장 */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                    <div
                      style={{
                        width: 34,
                        height: 34,
                        borderRadius: 10,
                        flexShrink: 0,
                        position: 'relative',
                        background: 'var(--md-sys-color-primary-container)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Sym
                        name={r.farm.icon ?? 'agriculture'}
                        size={19}
                        fill={1}
                        style={{ color: 'var(--md-sys-color-on-primary-container)' }}
                      />
                      <span
                        style={{
                          position: 'absolute',
                          top: -3,
                          right: -3,
                          width: 11,
                          height: 11,
                          borderRadius: 9999,
                          background:
                            r.status === 'ok'
                              ? 'var(--gs-primary-50)'
                              : r.status === 'warn'
                                ? 'var(--gs-warning)'
                                : '#9AA095',
                          border: '2px solid var(--md-sys-color-surface-container-lowest)',
                        }}
                      />
                    </div>
                    <div
                      style={{
                        font: '600 13px/17px var(--gs-font-kr)',
                        color: 'var(--md-sys-color-on-surface)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {r.farm.name}
                    </div>
                  </div>

                  {/* 농장주 */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                    <Avatar name={r.owner?.name} size={26} color="var(--gs-secondary-90)" />
                    <div
                      style={{
                        font: '500 13px/16px var(--gs-font-kr)',
                        color: 'var(--md-sys-color-on-surface)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {r.owner?.name ?? '–'}
                    </div>
                  </div>

                  {/* 지역 · 작물 */}
                  <div style={{ minWidth: 0 }}>
                    <div
                      style={{
                        font: '500 13px/16px var(--gs-font-kr)',
                        color: 'var(--md-sys-color-on-surface)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {r.region}
                    </div>
                    <div
                      style={{
                        font: '400 11px/14px var(--gs-font-kr)',
                        color: 'var(--md-sys-color-on-surface-variant)',
                        marginTop: 2,
                      }}
                    >
                      {r.crop}
                    </div>
                  </div>

                  {/* 상태 */}
                  <div>
                    <StatusPill s={r.status} />
                  </div>

                  {/* 장비 */}
                  <span
                    className="gs-num"
                    style={{
                      font: '600 13px/16px var(--gs-font-kr)',
                      color: 'var(--md-sys-color-on-surface)',
                    }}
                  >
                    {r.devices.length}
                    <span
                      style={{
                        font: '400 11px/14px var(--gs-font-kr)',
                        color: 'var(--md-sys-color-on-surface-variant)',
                        marginLeft: 2,
                      }}
                    >
                      대
                    </span>
                  </span>

                  {/* 온라인 */}
                  <span
                    className="gs-num"
                    style={{
                      font: '600 13px/16px var(--gs-font-kr)',
                      color:
                        r.online === r.devices.length
                          ? 'var(--md-sys-color-on-surface)'
                          : r.online === 0
                            ? '#9AA095'
                            : 'var(--gs-warning)',
                    }}
                  >
                    {r.online}
                    <span
                      style={{
                        font: '400 11px/14px var(--gs-font-kr)',
                        color: 'var(--md-sys-color-on-surface-variant)',
                        marginLeft: 1,
                      }}
                    >
                      /{r.devices.length}
                    </span>
                  </span>

                  {/* 경고 */}
                  <span
                    className="gs-num"
                    style={{
                      font: '600 13px/16px var(--gs-font-kr)',
                      color: r.warns > 0 ? 'var(--gs-warning)' : 'var(--md-sys-color-on-surface-variant)',
                    }}
                  >
                    {r.warns}
                    <span
                      style={{
                        font: '400 11px/14px var(--gs-font-kr)',
                        color: 'var(--md-sys-color-on-surface-variant)',
                        marginLeft: 2,
                      }}
                    >
                      건
                    </span>
                  </span>
                </div>
              ))}

              {!rows.length && (
                <div
                  style={{
                    padding: 48,
                    textAlign: 'center',
                    font: '400 14px/20px var(--gs-font-kr)',
                    color: 'var(--md-sys-color-on-surface-variant)',
                  }}
                >
                  조건에 맞는 농장이 없습니다
                </div>
              )}
            </div>
          </>
        )}
      </ViewScroll>

      {sel && (
        <FarmDrawer
          row={sel}
          onClose={() => {
            setSel(null);
            setSaveError(null);
          }}
          onSave={handleSave}
          saving={update.isPending}
          saveError={saveError}
        />
      )}

      <FarmFormModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSubmit={handleCreate}
        isLoading={create.isPending}
      />
    </>
  );
}

/* ---- 농장 상세 드로어 -------------------------------------------------- */
function FarmDrawer({
  row,
  onClose,
  onSave,
  saving,
  saveError,
}: {
  row: FarmRow;
  onClose: () => void;
  onSave: (id: string, body: UpdateFarmInput) => void;
  saving: boolean;
  saveError: string | null;
}): React.ReactElement {
  const { farm, owner, devices, region, crop, status } = row;

  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(farm.name);
  const [description, setDescription] = useState(farm.description ?? '');
  const [cropType, setCropType] = useState(farm.cropType ?? '');
  const [icon, setIcon] = useState<string>(farm.icon ?? 'agriculture');
  const [location, setLocation] = useState<GeoLocation | null>(farm.location ?? null);

  const resetForm = React.useCallback(() => {
    setName(farm.name);
    setDescription(farm.description ?? '');
    setCropType(farm.cropType ?? '');
    setIcon(farm.icon ?? 'agriculture');
    setLocation(farm.location ?? null);
  }, [farm]);

  React.useEffect(() => {
    resetForm();
    setEditing(false);
  }, [farm.id, resetForm]);

  const wasSaving = React.useRef(false);
  React.useEffect(() => {
    if (wasSaving.current && !saving && !saveError) {
      setEditing(false);
    }
    wasSaving.current = saving;
  }, [saving, saveError]);

  const submit = (): void => {
    onSave(farm.id, {
      name: name.trim(),
      description: description.trim() || undefined,
      cropType: cropType.trim() || undefined,
      icon,
      location: location ?? null,
    });
  };

  const FIELD: React.CSSProperties = {
    width: '100%',
    height: 38,
    padding: '0 12px',
    borderRadius: 10,
    border: '1px solid var(--md-sys-color-outline-variant)',
    background: 'var(--md-sys-color-surface-container-lowest)',
    color: 'var(--md-sys-color-on-surface)',
    font: '400 13px/1 var(--gs-font-kr)',
    boxSizing: 'border-box',
  };
  const FLABEL: React.CSSProperties = {
    font: '400 12px/16px var(--gs-font-kr)',
    color: 'var(--md-sys-color-on-surface-variant)',
    marginBottom: 4,
    display: 'block',
  };

  return (
    <>
      <div
        onClick={onClose}
        style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.32)', zIndex: 20 }}
      />
      <aside
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          width: 420,
          zIndex: 21,
          background: 'var(--md-sys-color-surface)',
          borderLeft: '1px solid var(--md-sys-color-outline-variant)',
          boxShadow: '-12px 0 40px -12px rgba(0,0,0,0.3)',
          display: 'flex',
          flexDirection: 'column',
          animation: 'drawerIn 240ms cubic-bezier(.2,0,0,1)',
        }}
      >
        <div
          style={{
            padding: '18px 20px',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            borderBottom: '1px solid var(--md-sys-color-outline-variant)',
          }}
        >
          <span style={{ font: '700 16px/22px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface)', flex: 1 }}>
            농장 상세
          </span>
          {!editing && <IconButton icon="edit" size={20} onClick={() => setEditing(true)} />}
          <IconButton icon="close" size={22} onClick={onClose} />
        </div>

        <div
          className="gs-scroll-hide"
          style={{ flex: 1, overflowY: 'auto', padding: '22px 20px', display: 'flex', flexDirection: 'column', gap: 20 }}
        >
          {editing ? (
            /* ── 수정 폼 ─────────────────────────────────────────── */
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <label style={FLABEL}>이름 *</label>
                <input value={name} onChange={(e) => setName(e.target.value)} maxLength={100} style={FIELD} />
              </div>
              <div>
                <label style={FLABEL}>작물</label>
                <input
                  value={cropType}
                  onChange={(e) => setCropType(e.target.value)}
                  maxLength={100}
                  placeholder="예: 토마토 · 딸기"
                  style={FIELD}
                />
              </div>
              <div>
                <label style={FLABEL}>아이콘</label>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {FARM_ICONS.map((ic) => (
                    <button
                      key={ic}
                      type="button"
                      aria-label={ic}
                      onClick={() => setIcon(ic)}
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: 10,
                        cursor: 'pointer',
                        border:
                          icon === ic
                            ? '2px solid var(--md-sys-color-primary)'
                            : '1px solid var(--md-sys-color-outline-variant)',
                        background: 'var(--md-sys-color-surface-container-lowest)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Sym name={ic} size={20} fill={1} style={{ color: 'var(--md-sys-color-on-surface)' }} />
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label style={FLABEL}>위치 · 주소</label>
                <LocationPicker value={location} onChange={setLocation} />
              </div>
              <div>
                <label style={FLABEL}>설명</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                  maxLength={500}
                  placeholder="농장에 대한 메모"
                  style={{ ...FIELD, height: 'auto', padding: '10px 12px', resize: 'vertical' }}
                />
              </div>
            </div>
          ) : (
            /* ── 표시(읽기) ─────────────────────────────────────── */
            <>
              {/* identity */}
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
                <div
                  style={{
                    width: 52,
                    height: 52,
                    borderRadius: 15,
                    flexShrink: 0,
                    background: 'var(--md-sys-color-primary-container)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Sym name={farm.icon ?? 'agriculture'} size={28} fill={1} style={{ color: 'var(--md-sys-color-on-primary-container)' }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ font: '700 19px/24px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface)' }}>
                    {farm.name}
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                      marginTop: 4,
                      font: '400 13px/18px var(--gs-font-kr)',
                      color: 'var(--md-sys-color-on-surface-variant)',
                    }}
                  >
                    <Sym name="location_on" size={14} />
                    {farm.location?.address ?? region} · {crop}
                  </div>
                </div>
                <StatusPill s={status} />
              </div>

              {/* owner card */}
              {owner && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '12px 14px',
                    borderRadius: 14,
                    background: 'var(--md-sys-color-surface-container-low)',
                    border: '1px solid var(--md-sys-color-outline-variant)',
                  }}
                >
                  <Avatar name={owner.name} size={40} color="var(--gs-secondary-90)" />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ font: '600 14px/18px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface)' }}>
                        {owner.name}
                      </span>
                      <RolePill role={owner.role === 'OPERATOR' ? 'manager' : 'user'} />
                    </div>
                    <div
                      className="gs-num"
                      style={{ font: '400 12px/16px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)', marginTop: 2 }}
                    >
                      {owner.phone ?? owner.email ?? '연락처 미등록'}
                    </div>
                  </div>
                  <IconButton icon="call" size={20} />
                </div>
              )}

              {/* device list */}
              <div>
                <div
                  style={{
                    font: '600 11px/14px var(--gs-font-kr)',
                    letterSpacing: 0.5,
                    textTransform: 'uppercase',
                    color: 'var(--md-sys-color-primary)',
                    marginBottom: 10,
                  }}
                >
                  장비 {devices.length}대
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {devices.map((d) => (
                    <div
                      key={d.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        padding: '12px 14px',
                        borderRadius: 14,
                        background: 'var(--md-sys-color-surface-container-low)',
                        border: '1px solid var(--md-sys-color-outline-variant)',
                      }}
                    >
                      <StatusDot s={d.ui} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            font: '600 13px/17px var(--gs-font-kr)',
                            color: 'var(--md-sys-color-on-surface)',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {d.name}
                        </div>
                        <div
                          className="gs-num"
                          style={{ font: '400 11px/14px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)', marginTop: 2 }}
                        >
                          {d.id}
                        </div>
                      </div>
                      <ModelTag m={d.model ?? 1} />
                    </div>
                  ))}
                  {!devices.length && (
                    <div
                      style={{
                        padding: '16px 14px',
                        borderRadius: 14,
                        background: 'var(--md-sys-color-surface-container-low)',
                        border: '1px solid var(--md-sys-color-outline-variant)',
                        font: '400 13px/18px var(--gs-font-kr)',
                        color: 'var(--md-sys-color-on-surface-variant)',
                        textAlign: 'center',
                      }}
                    >
                      등록된 장비가 없습니다
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        {/* footer (수정 모드 전용) */}
        {editing && (
          <div
            style={{
              padding: '14px 20px',
              borderTop: '1px solid var(--md-sys-color-outline-variant)',
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}
          >
            {saveError && (
              <div style={{ font: '400 12px/16px var(--gs-font-kr)', color: 'var(--md-sys-color-error)' }}>
                {saveError}
              </div>
            )}
            <div style={{ display: 'flex', gap: 10 }}>
              <Button
                variant="text"
                onClick={() => {
                  resetForm();
                  setEditing(false);
                }}
                disabled={saving}
                style={{ flex: 1, height: 42 }}
              >
                취소
              </Button>
              <Button variant="filled" icon="save" disabled={saving || !name.trim()} onClick={submit} style={{ flex: 1, height: 42 }}>
                {saving ? '저장 중…' : '저장'}
              </Button>
            </div>
          </div>
        )}
      </aside>
    </>
  );
}
